import { LucideGauge } from "lucide-react"
import { useMemo, useState } from "react"
import { aIsSpeedOpen, aPlaybackSpeed, useAtom } from "~/atoms"
import { Tooltip } from "~/components/ui"
import useIsPhone from "~/hooks/useIsPhone"
import { cn } from "~/utils"
import { ControlButton } from "./control-button"
import ControlPopoverContent from "./control-popover-content"

const SPEED_LABELS: { [key: number]: string } = {
  0.5: "0.5x",
  0.75: "0.75x",
  1: "1x",
  1.25: "1.25x",
  1.5: "1.5x",
}

export default function SpeedControl({ className }: { className?: string }) {
  const [isSpeedOpen, setIsSpeedOpen] = useAtom(aIsSpeedOpen)
  const [playbackSpeed, setPlaybackSpeed] = useAtom(aPlaybackSpeed)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const isPhone = useIsPhone()

  const options = useMemo(() => {
    return [
      { label: "0.5x", value: 0.5, selected: playbackSpeed === 0.5 },
      { label: "0.75x", value: 0.75, selected: playbackSpeed === 0.75 },
      { label: "1x", value: 1, selected: playbackSpeed === 1 },
      { label: "1.25x", value: 1.25, selected: playbackSpeed === 1.25 },
      { label: "1.5x", value: 1.5, selected: playbackSpeed === 1.5 },
    ]
  }, [playbackSpeed])

  return (
    <div className="relative flex items-center">
      <Tooltip.Provider>
        <Tooltip delayDuration={200} open={isTooltipOpen}>
          <Tooltip.Trigger asChild>
            <ControlButton
              className={cn(className, isPhone && "!h-8 !px-3")}
              aria-label={`Playback speed: ${SPEED_LABELS[playbackSpeed]}`}
              aria-expanded={isSpeedOpen}
              aria-haspopup="menu"
              onClick={(e) => {
                e.stopPropagation()
                setIsSpeedOpen(!isSpeedOpen)
                setIsTooltipOpen(false)
              }}
              onMouseOver={() => { if (!isSpeedOpen) setIsTooltipOpen(true) }}
              onMouseLeave={() => setIsTooltipOpen(false)}
              onFocus={() => { if (!isSpeedOpen) setIsTooltipOpen(true) }}
              onBlur={() => setIsTooltipOpen(false)}
            >
              <LucideGauge className="h-6 w-6 text-white" />
            </ControlButton>
          </Tooltip.Trigger>
          <Tooltip.Content>{SPEED_LABELS[playbackSpeed]}</Tooltip.Content>
        </Tooltip>
      </Tooltip.Provider>
      <ControlPopoverContent<number>
        title="Playback Speed"
        isOpen={isSpeedOpen}
        onClose={() => setIsSpeedOpen(false)}
        options={options}
        onClick={(speed) => {
          setPlaybackSpeed(speed)
          setIsSpeedOpen(false)
        }}
      />
    </div>
  )
}
